export type AdminFeedbackType =
  | 'good_answer'
  | 'bad_answer'
  | 'missing_knowledge'
  | 'wrong_intent'
  | 'should_escalate'
  | 'ui_bug'
  | 'backend_bug';

export type FeedbackSeverity = 'low' | 'medium' | 'high';

export const FEEDBACK_STATUSES = [
  'new',
  'triaged',
  'knowledge_created',
  'eval_added',
  'github_issue_created',
  'resolved',
  'rejected',
] as const;

export type FeedbackStatus = (typeof FEEDBACK_STATUSES)[number];

export type AdminFeedback = {
  id: string;
  conversationId: string;
  messageId: string;
  userMessage: string;
  assistantAnswer: string;
  type: AdminFeedbackType;
  severity: FeedbackSeverity;
  correctedAnswer?: string;
  expectedIntent?: string;
  notes?: string;
  status: FeedbackStatus;
  githubIssueUrl?: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
};

export type FeedbackClassification =
  | 'knowledge_gap'
  | 'wrong_policy'
  | 'bad_answer_quality'
  | 'wrong_intent'
  | 'missing_escalation'
  | 'tool_error'
  | 'ui_bug'
  | 'backend_bug'
  | 'product_data_issue'
  | 'duplicate'
  | 'not_actionable';

export type FeedbackTriageResult = {
  id: string;
  feedbackId: string;
  classification: FeedbackClassification;
  confidence: number;
  isCodeIssue: boolean;
  isKnowledgeIssue: boolean;
  shouldCreateEval: boolean;
  shouldCreateGithubIssue: boolean;
  shouldEscalationRule: boolean;
  suggestedTitle: string;
  suggestedSummary: string;
  suggestedFix: string;
  suggestedGithubIssueBody?: string;
  suggestedEvalCase?: {
    input: string;
    expectedIntent?: string;
    expectedAnswer?: string;
    mustMention?: string[];
    mustNotMention?: string[];
  };
  createdAt: string;
};

export type CreateFeedbackRequest = {
  conversationId: string;
  messageId?: string;
  userMessage: string;
  assistantAnswer: string;
  type: AdminFeedbackType;
  severity: FeedbackSeverity;
  correctedAnswer?: string;
  expectedIntent?: string;
  notes?: string;
  source?: 'playground' | 'session';
};

export type CreateFeedbackResponse = {
  feedbackId: string;
  status: 'new';
};

export type FeedbackListItem = {
  id: string;
  type: AdminFeedbackType;
  severity: FeedbackSeverity;
  status: FeedbackStatus;
  userMessage: string;
  classification?: FeedbackClassification;
  createdAt: string;
};

export type ListFeedbackResponse = {
  items: FeedbackListItem[];
  nextCursor?: string;
  total: number;
};

export type FeedbackDetailResponse = {
  feedback: AdminFeedback;
  triageResult?: FeedbackTriageResult;
  conversationMessages: Array<{
    role: 'user' | 'assistant';
    content: string;
    createdAt: string;
  }>;
};

export type ListFeedbackParams = {
  status?: FeedbackStatus;
  type?: AdminFeedbackType;
  severity?: FeedbackSeverity;
  classification?: string;
  limit?: number;
  cursor?: string;
};
